import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useFormatter, useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import Button from "~/components/common/button";
import myaccount from "~/components/common/myaccount.module.scss";
import LoginAsCustomer from "~/components/login/login-as-customer";
import { useAuthContext } from "~/contexts/auth";
import useSubscriptions from "~/hooks/use-subscriptions";
import Logger from "~/services/logger/logger";
import { basicAnimationStates } from "~/utils/animation-utils";
import appRoutes from "~/utils/app-routes";
import Constants from "~/utils/constants";

import EditPassword from "./edit-password";
import PersonalInfoForm from "./personal-info-form";
import styles from "./settings.module.scss";
import UpdateEmail from "./update-email";

export default function Settings() {
  const t = useTranslations();

  const { userSubscriptions, userActiveSubscriptions, removeSubscriptionsByFlags } = useSubscriptions();
  const formatter = useFormatter();

  const marketingSubscription = userActiveSubscriptions.find((f) => {
    return f.Subscription?.BackendID === Constants.MARKETING_OPT_IN_BACKEND_ID;
  });

  const [isEditSettingsOpen, setIsEditSettingsOpen] = useState(false);

  const [isEditPasswordOpen, setIsEditPasswordOpen] = useState(false);
  const [editPasswordSuccess, setEditPasswordSuccess] = useState(false);

  const [unsubscribing, setUnsubscribing] = useState(false);
  const { user } = useAuthContext();

  const unsubscribe = () => {
    setUnsubscribing(!0);
    removeSubscriptionsByFlags({
      MarketingOptIn: !1,
    })
      .then((r) => {
        Logger.instance.debug("unsubscribe newsletter success", r);
      })
      .finally(() => {
        Logger.instance.debug("unsubscribe newsletter finalized");
        setUnsubscribing(!1);
      });
  };

  const [submitSuccessful, setSubmitSuccessful] = useState(false);

  // resets submitSuccessful after 5 sec
  useEffect(() => {
    let timeOut: NodeJS.Timeout;
    if (submitSuccessful) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      timeOut = setTimeout(() => {
        setSubmitSuccessful(false);
      }, 5000);
    }
    return () => clearTimeout(timeOut);
  }, [submitSuccessful]);

  return (
    <>
      <LoginAsCustomer></LoginAsCustomer>
      <div className={classNames(myaccount.myaccountCard, styles.settingsCard)}>
        <div className={myaccount.myaccountCardHeader}>
          <h2>{t("generic.myaccount.settings.access_data")}</h2>
        </div>
        <div className={classNames(myaccount.myaccountCardContent, styles.accessData)}>
          <div className={styles.accessDataEmail}>
            <h3 className={myaccount.titleXs}>{t("generic.email")}</h3>
            <p>{user?.EmailAddress}</p>
            <Button
              lookAsLink
              onClick={() => {
                setIsEditSettingsOpen(true);
              }}
            >
              {t("generic.myaccount.settings.change_email")}
            </Button>
          </div>
          <div className={styles.accessDataPassword}>
            <h3 className={myaccount.titleXs}>{t("generic.form.password_label")}</h3>
            <p>***********</p>
            <Button
              lookAsLink
              onClick={() => {
                setIsEditPasswordOpen(true);
                setEditPasswordSuccess(false);
              }}
            >
              {t("generic.myaccount.settings.changePassword")}
            </Button>
            {editPasswordSuccess ? (
              <p className={styles.editSuccess} role="alert">
                {t("generic.myaccount.settings.editPasswordSuccess")}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      <div className={classNames(myaccount.myaccountCard, styles.settingsCard)}>
        <div className={myaccount.myaccountCardHeader}>
          <h2>{t("generic.myaccount.settings.personal_info")}</h2>
          <AnimatePresence>
            {submitSuccessful && (
              <motion.p
                className={myaccount.successSubmit}
                initial={basicAnimationStates.off}
                animate={basicAnimationStates.on}
                exit={basicAnimationStates.off}
                aria-live="polite"
              >
                {t("generic.successfully_added")}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
        {user && (
          <div className={myaccount.myaccountCardContent}>
            <PersonalInfoForm user={user} onSubmitSuccess={() => setSubmitSuccessful(true)} />
          </div>
        )}
      </div>
      <div className={classNames(myaccount.myaccountCard, styles.settingsCard)}>
        <div className={myaccount.myaccountCardHeader}>
          <h2>{t("generic.myaccount.settings.newsletter_subscription")}</h2>
        </div>
        <div className={myaccount.myaccountCardContent}>
          <ul className={styles.subscriptionsList}>
            {userSubscriptions.user?.length && marketingSubscription ? (
              <li className={styles.subscriptionsItem}>
                <p>
                  {t("generic.myaccount.settings.successfullySubscribed", {
                    subscription: formatter.dateTime(
                      new Date(marketingSubscription.LastModificationTime ?? marketingSubscription.CreationTime!)
                    ),
                  })}
                </p>
                <Button onClick={() => unsubscribe()} loading={unsubscribing} lookAsLink>
                  {t("generic.myaccount.settings.unsubscribe_cta")}
                </Button>
              </li>
            ) : (
              <p>{t("generic.myaccount.settings.no_newsletter_subscription")}</p>
            )}
          </ul>
        </div>
      </div>
      <div className={classNames(myaccount.myaccountCard, styles.settingsCard)}>
        <div className={myaccount.myaccountCardHeader}>
          <h2>{t("generic.myaccount.settings.account_deletion")}</h2>
        </div>
        <div className={classNames(myaccount.myaccountCardContent, styles.deleteAccount)}>
          <p>
            {t.rich("generic.myaccount.settings.account_deletion_description", {
              br: () => <br />,
              link: (chunks) => {
                return (
                  <Link href={appRoutes.CUSTOMER_HELP_CENTER} className={styles.link}>
                    {chunks?.toString()}
                  </Link>
                );
              },
            })}
          </p>
        </div>
      </div>
      <UpdateEmail isOpen={isEditSettingsOpen} onClose={() => setIsEditSettingsOpen(false)} />
      <EditPassword
        isOpen={isEditPasswordOpen}
        onClose={() => setIsEditPasswordOpen(false)}
        setEditPasswordSuccess={setEditPasswordSuccess}
      />
    </>
  );
}
